import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import classNames from 'classnames';
import store from 'store';
import { useSelector, useDispatch } from 'react-redux';
import PageContainer from '../PageContainer';
import BodyPane from './BodyPane';
import BinPane from './BinPane';
import PartPane from './PartPane';
import QuantityPane from './QuantityPane';
import Review from './ReviewCycle';
import FlatButton from './FlatButton';
import Footer from './Footer';
import ApiService from '../../services/ApiService';
import toastr from '../toastr';
import { modal } from '../modal/ModalEmitter';

const defaultListItem = (bin = '') => {
  return { bin, qty: 1, part: '', qtyLines: [], threshold: null, done: false, saved: false, skipped: false };
};

const CycleCounting = () => {
  const [list, setList] = useState([defaultListItem()]);
  const [listIndex, setListIndex] = useState(0);
  const [review, setReview] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [partList, setPartList] = useState([]);
  const fullscreen = useSelector(state => state.page.fullscreen);
  const dispatch = useDispatch();

  // Animations between steps
  const sectionTransitions = useTransition(currentStep, p => p, {
    from: { opacity: 0, transform: 'translate3d(100%,0,0)' },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: { opacity: 0, transform: 'translate3d(-50%,0,0)' },
  });

  const steps = [
    { name: 'bin', label: 'Choose Bin', icon: 'inbox' },
    { name: 'part', label: 'Scan Part', icon: 'wrench' },
    { name: 'qty', label: 'Quantity', icon: 'plus-circle-o-duplicate' },
  ];

  // Animation for review screen
  const reviewTransitions = useTransition(review, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  // Helpers
  const currentItem = list[listIndex];
  const doneCount = list.filter(item => item.done).length;
  const onLastStep = currentStep === steps.length - 1;
  const onFirstStep = currentStep === 0;
  // const firstStepIsSet = currentItem[steps[currentStep].name] !== '';

  const switchTo = toStep => {
    setCurrentStep(toStep);
  };

  const submit = newList => {
    const useList = newList || list;
    const done = useList.filter(item => item.done && !item.saved);

    if (!done.length) {
      toastr.info('No Action Taken', 'Everything has already been saved');
      return;
    }

    ApiService.put('/item/PBM2', done)
      .then(() => {
        const newList = useList.map(item => {
          if (!item.done || item.saved) {
            return item;
          }

          return {
            ...item,
            saved: true,
          };
        });

        setList(newList);
        const saved = done.map(p => `${p.bin} > ${p.part} > ${p.qty}`);
        toastr.success('Saved', saved.join(' \n'));
        store.set('pbm2.restore', []);
      })
      .catch(error => {
        const { response } = error;

        const responses = _.get(response, 'data.responses');

        const flagged = [];
        let message = '';
        if (responses) {
          for (const issue of responses) {
            if (issue.error === 'Part not in bin' || issue.error === 'Part not found') {
              flagged.push(parseInt(issue.row, 10));
              message = `${message}[${done[issue.row].bin}] ${done[issue.row].part} - ${issue.error}. `;
            }
          }
        }

        if (!flagged.length) {
          toastr.error('Error Saving', error);
          // Put unsaved entries into local storage for recovery
          store.set('pbm2.restore', done);
          return;
        }

        toastr.warn('Not Saved', message, { timeOut: 5000 });

        // TODO Not the best
        const newList = useList.map(item => {
          if (!item.done || item.saved) {
            return item;
          }

          for (const [index, bad] of done.entries()) {
            if (flagged.includes(index)) {
              if (bad.part === item.part && bad.bin === item.bin) {
                return {
                  ...item,
                  saved: true,
                  skipped: true,
                };
              }
            }
          }

          return {
            ...item,
            // Didn't save the good entries on the back end b/c something failed, better if we saved the good ones
            // until we do this needs to be false
            saved: false,
          };
        });

        setList(newList);
      });
  };

  const next = () => {
    // On last step
    if (onLastStep) {
      if (currentItem.done) {
        // Check and create new item for editing
        if (!list[listIndex + 1]) {
          setList([...list, defaultListItem(currentItem.bin)]);
          submit([...list, defaultListItem(currentItem.bin)]);
          setListIndex(listIndex + 1);

          // Just goto next item since it exists
        } else {
          setListIndex(listIndex + 1);
          submit();
        }

        // Start on Part step (second step)
        // Only re-enter Bin (first step) when user goes there explicitly
        setCurrentStep(1);

        return;
      }
    }

    setCurrentStep(prevStep => prevStep + 1);
  };

  const back = () => {
    if (!onFirstStep) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  };

  const updateBin = newBin => {
    const newList = list.map((item, index) => {
      if (index !== listIndex) {
        return item;
      }

      if (item.bin === newBin) {
        return item;
      }

      return {
        ...item,
        bin: newBin,
        part: '', // Reset Part when part is set
        done: false,
      };
    });

    setList(newList);
    next();
  };

  const updatePart = (newPart, allBins = [], threshold) => {
    const newList = list.map((item, index) => {
      if (index !== listIndex) {
        return item;
      }

      if (item.part === newPart) {
        return item;
      }

      let done = false;
      if (item.bin) {
        done = true;
      }

      return {
        ...item,
        part: newPart,
        allBins,
        qtyLines: [],
        threshold,
        done,
      };
    });

    setList(newList);
    next();
  };

  const updateQuantity = (newQuantity, qtyLines = []) => {
    const newList = list.map((item, index) => {
      if (index !== listIndex) {
        return item;
      }

      let done = false;
      if (item.part && item.bin) {
        done = true;
      }

      return {
        ...item,
        qty: newQuantity,
        qtyLines,
        done,
      };
    });

    setList(newList);
  };

  const getOpenIndex = () => {
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].done === false) {
        return i;
      }
    }

    setList([...list, defaultListItem(currentItem.bin)]);
    return list.length;
  };

  const edit = (index = 0, section = 0) => {
    if (index === 'last') {
      setListIndex(getOpenIndex());
      setCurrentStep(0);
      setReview(false);
      return;
    }

    setCurrentStep(section);
    setListIndex(index);
    setReview(false);
  };

  const remove = removeIndex => {
    if (removeIndex === undefined) {
      return;
    }

    const newList = list.filter((item, index) => {
      return removeIndex !== index;
    });

    setList(newList);

    if (!newList[listIndex]) {
      setListIndex(newList.length - 1);
      setCurrentStep(0);
    }
  };

  const getPreviousPart = () => {
    if (listIndex === 0) {
      return '';
    }

    return list[listIndex - 1].part;
  };

  useEffect(() => {
    if (doneCount === 0) {
      setListIndex(list.length - 1);
      setReview(false);
    }

    const newPartList = partList;
    list.forEach(item => {
      partList.forEach(part => {
        if (part.partNumber === item.part) {
          part.info = 'Recent';
        }
      });
    });
    setPartList(newPartList);
  }, [list]);

  useEffect(() => {
    const unsaved = store.get('pbm2.restore');

    const onRestore = () => {
      setList(unsaved);
      setReview(true);
    };

    if (unsaved && unsaved.length) {
      modal.confirm({
        confirmLabels: ['Restore', 'Forget'],
        callback: onRestore,
        title: 'Restore Session',
        message: `Would you like to restore ${unsaved.length} unsaved items?`,
      });
    }
  }, []);

  const renderBottomToolbar = () => {
    const buttons = [];

    if (!onFirstStep) {
      if (!(currentStep === 1 && list.length > 1)) {
        buttons.push(
          <FlatButton key="back" onClick={back}>
            Back
          </FlatButton>
        );
      }
    }

    const nextButton = (
      <FlatButton key="next" className="next" onClick={next}>
        Next
      </FlatButton>
    );

    const stepName = steps[currentStep].name;

    if (!onLastStep) {
      if (currentItem[stepName] !== '') {
        buttons.push(nextButton);
      }
    } else {
      if (currentItem[stepName] !== '' && currentItem.done === true) {
        buttons.push(nextButton);
      }
    }

    return buttons;
  };

  const getBinParts = binId => {
    return ApiService.get(`part/bin/${encodeURIComponent(binId)}`)
      .then(response => {
        const parts = response.data;
        if (parts.length && Array.isArray(parts)) {
          list.forEach((item, index) => {
            const isLast = list.length - 1 === index;
            parts.forEach(part => {
              if (part.partNumber === item.part) {
                if (isLast) {
                  part.info = 'Previous';
                } else {
                  part.info = 'Recent';
                }
              }
            });
          });
          setPartList(parts);
        }
      })
      .catch(() => {
        console.log('Problem getting parts in bin');
      });
  };

  const renderCurrentInfo = () => {
    if (onFirstStep || (currentItem && !currentItem.bin)) {
      return null;
    }

    return (
      <div className="current-item">
        <div className="set-item-label">Working Bin</div>
        <div className="set-item-value">
          {currentItem.bin} <i onClick={() => switchTo(0)} className="zmdi zmdi-edit" />
        </div>
      </div>
    );
  };

  const renderTopToolbar = () => {
    const toolbar = [];

    steps.forEach((step, index) => {
      const active = currentStep === index;

      // Disables Qty when Part is empty
      const disabled = currentStep === 1 && step.name === 'qty' && currentItem.part === '';
      const className = classNames({
        active,
        disabled,
      });

      toolbar.push(
        <FlatButton
          className={className}
          icon={step.icon}
          iconAlign="left"
          onClick={() => switchTo(index)}
          key={step.name}
        >
          {active === true ? step.label : ''}
        </FlatButton>
      );
    });

    return toolbar;
  };

  const sections = [
    (
      { style } // eslint-disable-line
    ) => (
      <animated.div style={style} className="scan-form scan-bin">
        {currentStep === 0 && (
          <BinPane initialValue={currentItem.bin} updateValue={updateBin} validationCallback={getBinParts} />
        )}
      </animated.div>
    ),
    (
      { style } // eslint-disable-line
    ) => (
      <animated.div style={style} className="scan-form scan-part">
        {currentStep === 1 && (
          <PartPane
            initialValue={currentItem.part}
            updateValue={updatePart}
            previous={getPreviousPart()}
            list={partList}
          />
        )}
      </animated.div>
    ),
    (
      { style } // eslint-disable-line
    ) => (
      <animated.div style={style} className="scan-form scan-quantity">
        {onLastStep && (
          // Conditional render is only because it's showing the next quantity before leave animation finishes
          <QuantityPane
            initialQty={currentItem.qty}
            initialQtyLines={currentItem.qtyLines}
            onChange={updateQuantity}
            allBins={currentItem.allBins}
            item={currentItem.part}
            bin={currentItem.bin}
          />
        )}
      </animated.div>
    ),
  ];

  const bottomToolbar = renderBottomToolbar();

  if (review) {
    return (
      <PageContainer disableSidebar>
        {reviewTransitions.map(
          ({ item, key, props }) =>
            item && ( // eslint-disable-line
              <animated.div className="scanner" key={key} style={props}>
                <Review edit={edit} remove={remove} list={list} submit={submit} />
              </animated.div>
            )
        )}
      </PageContainer>
    );
  }

  return (
    <PageContainer fullscreen disableSidebar>
      <div className="scanner">
        <div className={classNames('scan-head', { 'no-part': !currentItem.bin })}>{renderTopToolbar()}</div>
        <BodyPane sections={sections} transitions={sectionTransitions} step={currentStep} />
        {renderCurrentInfo()}
        <Footer>
          {doneCount !== 0 && (
            <FlatButton className="review" icon="view-list" iconAlign="left" onClick={() => setReview(true)}>
              {doneCount}
            </FlatButton>
          )}
          <FlatButton
            className={classNames('fullscreen', { active: fullscreen })}
            icon="fullscreen"
            onClick={() => dispatch({ type: 'TOGGLE_FULLSCREEN' })}
          />
          <div className="scan-toolbar">{bottomToolbar}</div>
        </Footer>
        <div className="invis-footer" />
      </div>
    </PageContainer>
  );
};

export default CycleCounting;
